import { useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  Building2,
  Check,
  HelpCircle,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Route,
  RotateCcw,
  SearchX,
  ShieldAlert,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useRoutesConfig, useRoutesMutation } from "@/hooks/useAdminUsers";
import type {
  AdminRouteRow,
  RoutesConfigResponse,
  RoutesDepartment,
  RouteSeverity,
} from "@/api/adminCenter";

/**
 * D-044 S3 — #6 Departments & routes (VISUAL v1.1: 2 панелі desktop, P7
 * severity-split, MP2 createable=confirm+server-gate, MP3 defaultAssignee
 * drawer-only, MP4 seed=рядок+агрегат+«Пересадити», MP8 empty-split, MP9
 * enriched assign-drawer, MP10 no drag). ABSORB IR Admin drawer (AC-050).
 * Дані/severity/counts/allowedActions = backend-owned (D-017).
 */

export function AdminRoutesPage() {
  const perms = useAuthStore((s) => s.permissions);
  const canView = !!perms?.canViewAdminCenter;
  const cfgQ = useRoutesConfig(canView);

  const [deptFilter, setDeptFilter] = useState<string | null>(null); // dept code
  const [sevFilter, setSevFilter] = useState<RouteSeverity | null>(null);
  const [assign, setAssign] = useState<{ route: AdminRouteRow; replaceUserId: string | null } | null>(null);
  const [headDept, setHeadDept] = useState<RoutesDepartment | null>(null);
  const [editDept, setEditDept] = useState<RoutesDepartment | "new" | null>(null);
  const [editRoute, setEditRoute] = useState<AdminRouteRow | "new" | null>(null);
  const [confirm, setConfirm] = useState<{ route: AdminRouteRow; enable: boolean } | null>(null);
  // D-044h
  const [deptActive, setDeptActive] = useState<{ dept: RoutesDepartment; active: boolean; routeCount: number } | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const showToast = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    window.setTimeout(() => setToast(null), 4000);
  };

  const toggleM = useRoutesMutation("toggleCreateable");
  const deptActiveM = useRoutesMutation("setDepartmentActive");

  const data = cfgQ.data;
  const allowed = data?.allowedActions ?? {};

  // Хук СТРОГО перед canView-guard-ом: perms підвантажуються async, і ранній
  // return до useMemo міняв кількість хуків між рендерами (React #310 — падало
  // на свіжій сесії, що відкривала /admin/routes напряму).
  const filtered = useMemo(() => {
    let rows = data?.routes ?? [];
    if (deptFilter !== null) rows = rows.filter((r) => r.departmentCode === deptFilter);
    if (sevFilter !== null) rows = rows.filter((r) => r.severity === sevFilter);
    return rows;
  }, [data, deptFilter, sevFilter]);

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px] rounded-lg border bg-muted/30 p-8 text-center">
          <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          <p className="text-sm font-medium">Немає доступу до Адмін-центру</p>
          <p className="mt-1 text-xs text-muted-foreground">Розділ доступний лише адміністраторам.</p>
        </div>
      </div>
    );
  }

  const requestCount = data ? data.routes.filter((r) => r.kind === "request").length : 0;
  const aeCount = data ? data.routes.length - requestCount : 0;
  const selectedDept = deptFilter !== null ? data?.departments.find((d) => d.code === deptFilter) : undefined;

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1100px] space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
              <Route className="h-5 w-5" /> Відділи та маршрути
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Маршрути заявок (#9) + посадка відповідальних. Без відповідального — заявки неможливі
              (NULL=DENY).
            </p>
          </div>
          <div className="flex gap-2">
            {/* MP7: allowedActions-gated */}
            {allowed.addDepartment && (
              <Button variant="outline" size="sm" onClick={() => setEditDept("new")}>
                <Plus className="mr-1 h-4 w-4" /> Відділ
              </Button>
            )}
            {allowed.addRoute && (
              <Button size="sm" onClick={() => setEditRoute("new")}>
                <Plus className="mr-1 h-4 w-4" /> Маршрут
              </Button>
            )}
          </div>
        </div>

        {/* MP4: агрегат severity (живить #7 Health) — клік = фільтр */}
        {data && (
          <div className="flex flex-wrap items-center gap-2">
            {data.aggregate.noResponsible > 0 && (
              <button
                type="button"
                onClick={() => setSevFilter(sevFilter === "error" ? null : "error")}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-badge bg-destructive-soft px-2.5 py-1 text-xs font-medium text-destructive",
                  sevFilter === "error" && "ring-1 ring-destructive",
                )}
              >
                <AlertCircle className="h-3.5 w-3.5" />
                {data.aggregate.noResponsible} без відповідального
              </button>
            )}
            {data.aggregate.seedAdmin > 0 && (
              <button
                type="button"
                onClick={() => setSevFilter(sevFilter === "warning" ? null : "warning")}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-badge bg-warning px-2.5 py-1 text-xs font-medium text-warning-foreground",
                  sevFilter === "warning" && "ring-1 ring-warning-foreground",
                )}
                title="Тимчасові seed-admin відповідальні (pilot) — клік: показати ці маршрути"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                {data.aggregate.seedAdmin} на seed-admin · Пересадити
              </button>
            )}
            {data.aggregate.noResponsible === 0 && data.aggregate.seedAdmin === 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-badge bg-success px-2.5 py-1 text-xs font-medium text-success-foreground">
                <Check className="h-3.5 w-3.5" /> усі маршрути налаштовані
              </span>
            )}
            {data.aggregate.noResponsible + data.aggregate.seedAdmin > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-badge bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                решта налаштовані
              </span>
            )}
          </div>
        )}

        {/* 2 панелі desktop / stacked mobile (MP10) */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[300px_1fr]">
          {/* ── Відділи ── */}
          <div className="h-fit rounded-card border bg-card p-3.5">
            <p className="mb-2 flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Відділи
              <span
                title="Head = призначає/ескалує запити. Responsible = бере запити в роботу."
                className="cursor-help text-muted-foreground"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </span>
            </p>
            {cfgQ.isLoading && (
              <div className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            )}
            {data && (
              <div className="divide-y">
                {data.departments.map((d) => (
                  <DeptRow
                    key={d.id}
                    dept={d}
                    selected={deptFilter === d.code}
                    createableRouteCount={data.routes.filter((r) => r.departmentCode === d.code && r.kind === "request").length}
                    onSelect={() => setDeptFilter(deptFilter === d.code ? null : d.code)}
                    onAssignHead={allowed.setHead ? () => setHeadDept(d) : undefined}
                    onEdit={allowed.editDepartment ? () => setEditDept(d) : undefined}
                    onSetActive={allowed.deactivateDepartment ? (active, n) => setDeptActive({ dept: d, active, routeCount: n }) : undefined}
                  />
                ))}
                {data.departments.length === 0 && (
                  <div className="py-6 text-center text-xs text-muted-foreground">
                    Відділів немає.
                    {allowed.addDepartment && (
                      <Button variant="outline" size="sm" className="ml-2" onClick={() => setEditDept("new")}>
                        <Plus className="mr-1 h-3.5 w-3.5" /> Відділ
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Маршрути ── */}
          <div className="rounded-card border bg-card p-3.5">
            <p className="mb-2 flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Маршрути заявок
              {data && (
                <span className="rounded-badge bg-muted px-2 py-0.5 normal-case tracking-normal text-muted-foreground">
                  {data.routes.length} live · {requestCount} заявок + {aeCount} ActionEngine
                </span>
              )}
            </p>

            {cfgQ.isLoading && (
              <div className="space-y-2">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            )}
            {cfgQ.isError && (
              <div className="p-4 text-sm text-destructive">Не вдалося завантажити маршрути.</div>
            )}

            {data && filtered.length === 0 && (
              // MP8: empty-split — dept-без-маршрутів vs фільтр-без-результатів
              selectedDept && sevFilter === null ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                  <Building2 className="mx-auto mb-2 h-5 w-5" />
                  Маршрутів для відділу «{selectedDept.name}» немає
                  {allowed.addRoute && (
                    <div className="mt-3">
                      <Button variant="outline" size="sm" onClick={() => setEditRoute("new")}>
                        <Plus className="mr-1 h-3.5 w-3.5" /> Додати маршрут
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                  <SearchX className="mx-auto mb-2 h-5 w-5" />
                  За фільтром нічого не знайдено
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDeptFilter(null);
                        setSevFilter(null);
                      }}
                    >
                      <RotateCcw className="mr-1 h-3.5 w-3.5" /> Скинути фільтр
                    </Button>
                  </div>
                </div>
              )
            )}

            {data && filtered.length > 0 && (
              <table className="w-full table-fixed border-collapse text-sm">
                <colgroup>
                  <col className="w-[26%]" />
                  <col className="w-[17%]" />
                  <col className="w-[27%]" />
                  <col className="w-[16%]" />
                  <col className="w-[14%]" />
                </colgroup>
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-2 py-2 font-medium">Тип заявки</th>
                    <th className="px-2 py-2 font-medium">Відділ</th>
                    <th className="px-2 py-2 font-medium">Відповідальний</th>
                    <th className="px-2 py-2 font-medium">Приймання</th>
                    <th className="px-2 py-2 font-medium">Тип</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <RouteRow
                      key={r.id}
                      route={r}
                      canToggle={!!allowed.toggleCreateable}
                      canAssign={!!allowed.assignResponsible}
                      canEdit={!!allowed.editRoute}
                      toggling={toggleM.isPending && confirm === null}
                      onToggle={(enable) => setConfirm({ route: r, enable })}
                      onAssign={(replaceUserId) => setAssign({ route: r, replaceUserId })}
                      onEdit={() => setEditRoute(r)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* MP2: createable toggle = CONFIRM dialog (no instant) */}
      {confirm && (
        <ConfirmCreateableDialog
          route={confirm.route}
          enable={confirm.enable}
          busy={toggleM.isPending}
          onCancel={() => setConfirm(null)}
          onYes={() =>
            toggleM.mutate(
              { routeId: confirm.route.id, enable: confirm.enable },
              {
                onSuccess: () => {
                  showToast(
                    "ok",
                    confirm.enable
                      ? `«${confirm.route.label}» — приймання заявок увімкнено`
                      : `«${confirm.route.label}» — приймання заявок вимкнено`,
                  );
                  setConfirm(null);
                },
                onError: (e) => {
                  showToast("err", e instanceof Error ? e.message : "Помилка");
                  setConfirm(null);
                },
              },
            )
          }
        />
      )}

      {/* D-044h: deactivate/activate confirm з route-count cascade */}
      {deptActive && (
        <ConfirmDeptActiveDialog
          state={deptActive}
          busy={deptActiveM.isPending}
          onCancel={() => setDeptActive(null)}
          onYes={() =>
            deptActiveM.mutate(
              { departmentId: deptActive.dept.id, active: deptActive.active },
              {
                onSuccess: () => {
                  showToast(
                    "ok",
                    deptActive.active
                      ? `Відділ «${deptActive.dept.name}» активовано`
                      : `Відділ «${deptActive.dept.name}» деактивовано — маршрути приховані з вибору`,
                  );
                  setDeptActive(null);
                },
                onError: (e) => {
                  showToast("err", e instanceof Error ? e.message : "Помилка");
                  setDeptActive(null);
                },
              },
            )
          }
        />
      )}

      {/* MP9: enriched assign-drawer */}
      {assign && data && (
        <AssignResponsibleDrawer
          route={assign.route}
          replaceUserId={assign.replaceUserId}
          cfg={data}
          onClose={() => setAssign(null)}
          onToast={showToast}
        />
      )}

      {headDept && data && (
        <HeadDrawer dept={headDept} cfg={data} onClose={() => setHeadDept(null)} onToast={showToast} />
      )}

      {editDept !== null && (
        <DeptFormDialog
          dept={editDept === "new" ? null : editDept}
          userOptions={data?.userOptions ?? []}
          onClose={() => setEditDept(null)}
          onToast={showToast}
        />
      )}

      {editRoute !== null && data && (
        <RouteFormDialog
          route={editRoute === "new" ? null : editRoute}
          departments={data.departments}
          presetDeptCode={editRoute === "new" ? deptFilter : null}
          onClose={() => setEditRoute(null)}
          onToast={showToast}
        />
      )}

      {toast && (
        <div
          role="status"
          className={cn(
            "fixed bottom-4 right-4 z-50 rounded-lg px-4 py-2.5 text-sm shadow-lg",
            toast.kind === "ok"
              ? "bg-success text-success-foreground"
              : "bg-destructive-soft text-destructive",
          )}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Відділи panel ───────────────────────────────────────────────────────────

function DeptRow({
  dept,
  selected,
  createableRouteCount,
  onSelect,
  onAssignHead,
  onEdit,
  onSetActive,
}: {
  dept: RoutesDepartment;
  selected: boolean;
  createableRouteCount: number;
  onSelect: () => void;
  onAssignHead?: () => void;
  onEdit?: () => void;
  onSetActive?: (active: boolean, routeCount: number) => void;
}) {
  return (
    // D-044h: деактивований відділ — сірий (collapsed-вигляд), але лишається в адмінці
    <div
      className={cn(
        "py-2",
        selected && "rounded-lg bg-primary-soft px-2 -mx-2",
        !dept.active && "opacity-60",
      )}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSelect}
          aria-pressed={selected}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          title={selected ? "Зняти фільтр за відділом" : "Показати маршрути відділу"}
        >
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5">
              <span className={cn("truncate text-sm", !dept.active && "text-muted-foreground line-through")}>
                {dept.name}
              </span>
              {!dept.active && (
                <span className="shrink-0 rounded-badge bg-muted px-1.5 py-px text-[10px] text-muted-foreground">
                  Деактивований
                </span>
              )}
            </span>
            <span className="block text-[10px] text-muted-foreground">
              {dept.orgType === "sales_scope" ? "sales-scope" : "functional-inbox"} ·{" "}
              {dept.responsibles.length} відп. · {dept.routeCount} марш.
            </span>
          </span>
        </button>
        {/* D-044h: Активувати/Деактивувати (allowedActions-gated) */}
        {onSetActive &&
          (dept.active ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-[11px] text-muted-foreground"
              aria-label={`Деактивувати відділ ${dept.name}`}
              title="Прибрати з вибору при створенні заявок (реверсивно)"
              onClick={() => onSetActive(false, createableRouteCount)}
            >
              Деактивувати
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-[11px]"
              aria-label={`Активувати відділ ${dept.name}`}
              onClick={() => onSetActive(true, createableRouteCount)}
            >
              Активувати
            </Button>
          ))}
        {onEdit && (
          <Button variant="ghost" size="sm" aria-label={`Редагувати відділ ${dept.name}`} onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <div className="ml-6 mt-1">
        {dept.headName ? (
          <span
            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
            title="Head: призначає/ескалує запити"
          >
            <Initials name={dept.headName} />
            head: {dept.headName}
            {dept.headIsSeed && (
              <span className="rounded-badge bg-warning px-1.5 py-px text-[10px] text-warning-foreground">seed</span>
            )}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-badge bg-warning px-2 py-0.5 text-[10px] text-warning-foreground">
              <AlertTriangle className="h-3 w-3" /> без head
            </span>
            {onAssignHead && (
              <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={onAssignHead}>
                Призначити
              </Button>
            )}
          </span>
        )}
        {dept.headName && onAssignHead && (
          <button
            type="button"
            onClick={onAssignHead}
            className="ml-2 text-[10px] text-muted-foreground underline hover:text-foreground"
          >
            змінити
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Route row (P7 severity-split) ──────────────────────────────────────────

function RouteRow({
  route: r,
  canToggle,
  canAssign,
  canEdit,
  toggling,
  onToggle,
  onAssign,
  onEdit,
}: {
  route: AdminRouteRow;
  canToggle: boolean;
  canAssign: boolean;
  canEdit: boolean;
  toggling: boolean;
  onToggle: (enable: boolean) => void;
  onAssign: (replaceUserId: string | null) => void;
  onEdit: () => void;
}) {
  const seedResp = r.responsibles.find((x) => x.isSeed);

  return (
    <tr className="border-t align-middle">
      <td className="px-2 py-2.5">
        {canEdit ? (
          <button type="button" onClick={onEdit} className="text-left text-sm underline-offset-2 hover:underline">
            {r.label}
          </button>
        ) : (
          <span className="text-sm">{r.label}</span>
        )}
        {!r.enabled && (
          <span className="ml-1.5 rounded-badge bg-muted px-1.5 py-px text-[10px] text-muted-foreground">вимкнено</span>
        )}
      </td>
      <td className="px-2 py-2.5 text-xs text-muted-foreground">{r.departmentName ?? r.departmentCode ?? "—"}</td>
      <td className="px-2 py-2.5">
        {r.severity === "error" && (
          <>
            <span className="inline-flex items-center gap-1 rounded-badge bg-destructive-soft px-2 py-0.5 text-xs text-destructive">
              <AlertCircle className="h-3 w-3" /> Без відповідального
            </span>
            <div className="mt-0.5 text-[11px] text-destructive">
              Заявки неможливі ·{" "}
              {canAssign && (
                <button type="button" onClick={() => onAssign(null)} className="underline">
                  Призначити
                </button>
              )}
            </div>
          </>
        )}
        {r.severity === "warning" && (
          <>
            <span className="inline-flex items-center gap-1.5 text-sm">
              <Initials name={r.responsibles[0]?.name ?? "?"} />
              {r.responsibles[0]?.name}
            </span>
            <div className="mt-0.5 flex items-center gap-1.5 text-[11px]">
              <span className="inline-flex items-center gap-1 rounded-badge bg-warning px-1.5 py-px text-[10px] text-warning-foreground">
                <AlertTriangle className="h-2.5 w-2.5" /> pilot seed
              </span>
              {canAssign && (
                <button
                  type="button"
                  onClick={() => onAssign(seedResp?.id ?? null)}
                  className="text-warning-foreground underline"
                >
                  Пересадити
                </button>
              )}
            </div>
          </>
        )}
        {r.severity === "ok" && (
          <span className="inline-flex items-center gap-1.5 text-sm">
            <Initials name={r.responsibles.find((x) => !x.isSeed)?.name ?? r.responsibles[0]?.name ?? "?"} />
            <span className="truncate">
              {r.responsibles.filter((x) => !x.isSeed).map((x) => x.name).join(", ") ||
                r.responsibles.map((x) => x.name).join(", ")}
            </span>
          </span>
        )}
      </td>
      <td className="px-2 py-2.5">
        {r.kind === "actionEngine" ? (
          // AC-052: ActionEngine-only = read-only, БЕЗ toggle
          <span className="inline-flex items-center gap-1 rounded-badge bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" /> read-only
          </span>
        ) : r.severity === "error" ? (
          <>
            <Toggle on={r.createable} disabled aria-label={`Приймання заявок: ${r.label}`} />
            <div className="mt-0.5 text-[10px] text-muted-foreground">
              Спершу призначте відповідального (NULL=DENY)
            </div>
          </>
        ) : (
          <>
            <Toggle
              on={r.createable}
              disabled={!canToggle || toggling}
              aria-label={`Приймання заявок: ${r.label}`}
              onClick={() => onToggle(!r.createable)}
            />
            {/* D-044h AC-059: явна мітка OFF-стану (не просто сірий тумблер) */}
            {!r.createable && (
              <div className="mt-0.5 text-[10px] text-muted-foreground">вимкнено · прихований з вибору</div>
            )}
          </>
        )}
      </td>
      <td className="px-2 py-2.5">
        {r.kind === "actionEngine" ? (
          <span
            className="rounded-badge bg-info px-2 py-0.5 text-xs text-info-foreground"
            title="Person-route ActionEngine. defaultAssignee — у деталях маршруту, не основна модель P0."
          >
            ActionEngine
          </span>
        ) : (
          <span className="rounded-badge bg-muted px-2 py-0.5 text-xs text-muted-foreground">Заявка #9</span>
        )}
      </td>
    </tr>
  );
}

function Toggle({
  on,
  disabled,
  onClick,
  "aria-label": ariaLabel,
}: {
  on: boolean;
  disabled?: boolean;
  onClick?: () => void;
  "aria-label": string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "relative h-5 w-9 rounded-full transition-colors",
        on ? "bg-primary" : "bg-border",
        disabled ? "cursor-not-allowed opacity-45" : "cursor-pointer",
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-[left]",
          on ? "left-[18px]" : "left-0.5",
        )}
      />
    </button>
  );
}

function Initials({ name }: { name: string }) {
  const ini = name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("");
  return (
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-info text-[9px] text-info-foreground">
      {ini || "?"}
    </span>
  );
}

// ─── MP2: createable confirm ────────────────────────────────────────────────

function ConfirmCreateableDialog({
  route,
  enable,
  busy,
  onCancel,
  onYes,
}: {
  route: AdminRouteRow;
  enable: boolean;
  busy: boolean;
  onCancel: () => void;
  onYes: () => void;
}) {
  const panelRef = useDrawerA11y(true, onCancel);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={enable ? "Увімкнути приймання заявок" : "Вимкнути маршрут"}
        className="w-full max-w-sm rounded-card border bg-card p-4 shadow-xl outline-none"
      >
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle className="h-4 w-4 text-warning-foreground" />
          {enable ? "Увімкнути приймання заявок?" : "Вимкнути маршрут?"}
        </h3>
        <p className="mb-4 text-sm text-muted-foreground">
          {enable
            ? `Увімкнути «${route.label}»? Маршрут стане доступний менеджерам у формі створення запиту.`
            : `Вимкнути «${route.label}»? Менеджери більше не зможуть створювати цей тип запиту.`}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Скасувати
          </Button>
          <Button size="sm" disabled={busy} onClick={onYes}>
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Підтвердити
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── D-044h: deactivate/activate confirm (route-count cascade) ──────────────

function ConfirmDeptActiveDialog({
  state,
  busy,
  onCancel,
  onYes,
}: {
  state: { dept: RoutesDepartment; active: boolean; routeCount: number };
  busy: boolean;
  onCancel: () => void;
  onYes: () => void;
}) {
  const panelRef = useDrawerA11y(true, onCancel);
  const { dept, active, routeCount } = state;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={active ? "Активувати відділ" : "Деактивувати відділ"}
        className="w-full max-w-sm rounded-card border bg-card p-4 shadow-xl outline-none"
      >
        <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle className="h-4 w-4 text-warning-foreground" />
          {active ? "Активувати відділ?" : "Деактивувати відділ?"}
        </h3>
        <p className="mb-4 text-sm text-muted-foreground">
          {active ? (
            <>
              Активувати «{dept.name}»? Відділ і його {routeCount} маршрут(ів) знову стануть
              доступні для створення заявок (у попередньому стані).
            </>
          ) : (
            <>
              Деактивувати «{dept.name}»? {routeCount} маршрут(ів) стануть недоступні для створення
              заявок (приховані з вибору). Сутності не видаляються — дію можна скасувати «Активувати».
            </>
          )}
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Скасувати
          </Button>
          <Button size="sm" disabled={busy} onClick={onYes}>
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {active ? "Активувати" : "Деактивувати"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── MP9: assign-responsible drawer ─────────────────────────────────────────

function AssignResponsibleDrawer({
  route,
  replaceUserId,
  cfg,
  onClose,
  onToast,
}: {
  route: AdminRouteRow;
  replaceUserId: string | null;
  cfg: RoutesConfigResponse;
  onClose: () => void;
  onToast: (k: "ok" | "err", m: string) => void;
}) {
  const panelRef = useDrawerA11y(true, onClose);
  const [userSel, setUserSel] = useState<string | null>(null);
  const m = useRoutesMutation("assignResponsible");

  const current = route.responsibles;
  const replacing = replaceUserId !== null ? current.find((x) => x.id === replaceUserId) : null;
  const candidates = cfg.userOptions.filter((u) => !current.some((c) => c.id === u.id));
  const unlocksCreateable = route.severity === "error";

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Призначити відповідального"
        className="absolute right-0 top-0 h-full w-[380px] overflow-y-auto border-l bg-card p-5 shadow-xl outline-none"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">
            {replacing ? "Пересадити відповідального" : "Призначити відповідального"}
          </h2>
          <Button variant="ghost" size="sm" aria-label="Закрити" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* MP9: route · dept · current · new · createable-impact */}
        <dl className="mb-3 divide-y text-xs">
          <Kv k="Маршрут" v={route.label} />
          <Kv k="Відділ" v={route.departmentName ?? route.departmentCode} />
          <Kv
            k={replacing ? "Кого заміняємо" : "Поточні відповідальні"}
            v={
              replacing
                ? `${replacing.name} (seed)`
                : current.length > 0
                  ? current.map((x) => x.name + (x.isSeed ? " (seed)" : "")).join(", ")
                  : "— (немає)"
            }
          />
        </dl>

        <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Новий відповідальний
        </p>
        <div className="max-h-[40vh] space-y-1.5 overflow-y-auto">
          {candidates.map((u) => (
            <button
              key={u.id}
              type="button"
              aria-pressed={userSel === u.id}
              onClick={() => setUserSel(u.id)}
              className={cn(
                "block w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                userSel === u.id ? "border-primary bg-primary-soft" : "hover:bg-accent",
              )}
            >
              {u.name}
              {u.isSeed && <span className="ml-1.5 text-[10px] text-muted-foreground">(admin — знову seed)</span>}
            </button>
          ))}
          {candidates.length === 0 && (
            <p className="text-xs text-muted-foreground">Немає кандидатів — усі вже відповідальні.</p>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Вплив на приймання</span>
          {unlocksCreateable ? (
            <span className="rounded-badge bg-success px-2 py-0.5 text-success-foreground">
              розблокує приймання заявок
            </span>
          ) : (
            <span className="rounded-badge bg-muted px-2 py-0.5 text-muted-foreground">без зміни</span>
          )}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Увімкнути приймання заявок можна лише з ≥1 відповідальним (NULL=DENY). Дію буде записано в
          аудит (actor / old / new / час).
        </p>

        <div className="mt-4 border-t pt-3.5">
          {userSel === null && (
            <p className="mb-2 rounded bg-destructive-soft px-2 py-1.5 text-xs text-destructive">Оберіть користувача</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Скасувати
            </Button>
            <Button
              size="sm"
              disabled={m.isPending || userSel === null || route.departmentId === null}
              onClick={() =>
                m.mutate(
                  { departmentId: route.departmentId!, userId: userSel!, replaceUserId },
                  {
                    onSuccess: () => {
                      onToast("ok", replacing ? "Відповідального пересаджено" : "Відповідального призначено");
                      onClose();
                    },
                    onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
                  },
                )
              }
            >
              {m.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Зберегти
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Head drawer (MP1: head ≠ responsible) ──────────────────────────────────

function HeadDrawer({
  dept,
  cfg,
  onClose,
  onToast,
}: {
  dept: RoutesDepartment;
  cfg: RoutesConfigResponse;
  onClose: () => void;
  onToast: (k: "ok" | "err", m: string) => void;
}) {
  const panelRef = useDrawerA11y(true, onClose);
  const [sel, setSel] = useState<string | null>(dept.headId);
  const m = useRoutesMutation("setHead");

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Призначити head відділу"
        className="absolute right-0 top-0 h-full w-[380px] overflow-y-auto border-l bg-card p-5 shadow-xl outline-none"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Head відділу «{dept.name}»</h2>
          <Button variant="ghost" size="sm" aria-label="Закрити" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Head призначає/ескалує запити відділу (НЕ responsible — той бере запити в роботу).
        </p>
        <div className="max-h-[55vh] space-y-1.5 overflow-y-auto">
          {cfg.userOptions.map((u) => (
            <button
              key={u.id}
              type="button"
              aria-pressed={sel === u.id}
              onClick={() => setSel(u.id)}
              className={cn(
                "block w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                sel === u.id ? "border-primary bg-primary-soft" : "hover:bg-accent",
              )}
            >
              {u.name}
            </button>
          ))}
          <button
            type="button"
            aria-pressed={sel === null}
            onClick={() => setSel(null)}
            className={cn(
              "block w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors",
              sel === null ? "border-primary bg-primary-soft" : "hover:bg-accent",
            )}
          >
            Зняти head
          </button>
        </div>
        <div className="mt-4 flex justify-end gap-2 border-t pt-3.5">
          <Button variant="outline" size="sm" onClick={onClose}>
            Скасувати
          </Button>
          <Button
            size="sm"
            disabled={m.isPending || sel === dept.headId}
            onClick={() =>
              m.mutate(
                { departmentId: dept.id, userId: sel },
                {
                  onSuccess: () => {
                    onToast("ok", "Head відділу оновлено");
                    onClose();
                  },
                  onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
                },
              )
            }
          >
            {m.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Зберегти
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── ABSORB: dept form (повний field-set старого IR-drawer) ─────────────────

function DeptFormDialog({
  dept,
  userOptions,
  onClose,
  onToast,
}: {
  dept: RoutesDepartment | null;
  userOptions: { id: string; name: string }[];
  onClose: () => void;
  onToast: (k: "ok" | "err", m: string) => void;
}) {
  const panelRef = useDrawerA11y(true, onClose);
  const m = useRoutesMutation("saveDepartment");
  const [f, setF] = useState({
    name: dept?.name ?? "",
    code: dept?.code ?? "",
    active: dept?.active ?? true,
    headId: dept?.headId ?? "",
    responsibleIds: dept?.responsibles.map((r) => r.id) ?? [],
    claimSla: dept?.claimSlaDefaultHours?.toString() ?? "24",
    completeSla: dept?.completeSlaDefaultHours?.toString() ?? "48",
    notifyInApp: dept?.notifyInApp ?? true,
    notifyEmail: dept?.notifyEmail ?? false,
  });

  const invalid =
    f.name.trim().length < 2
      ? "Вкажіть назву"
      : !/^[a-z0-9_]{2,60}$/.test(f.code.trim())
        ? "Код: лише a-z, 0-9, _ (2-60 символів)"
        : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={dept ? `Відділ ${dept.name}` : "Новий відділ"}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-card border bg-card p-4 shadow-xl outline-none"
      >
        <h3 className="mb-3 text-sm font-semibold">{dept ? `Відділ: ${dept.name}` : "Новий відділ"}</h3>
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Назва *</span>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Код * (a-z, 0-9, _)</span>
            <Input
              value={f.code}
              onChange={(e) => setF({ ...f, code: e.target.value })}
              className="font-mono"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={f.active}
              onChange={(e) => setF({ ...f, active: e.target.checked })}
            />
            Активний
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Head (призначає/ескалує)</span>
            <select
              value={f.headId}
              onChange={(e) => setF({ ...f, headId: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
            >
              <option value="">— немає —</option>
              {userOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">
              Відповідальні (≥1 — інакше маршрути ERROR / заявки неможливі)
            </span>
            <select
              multiple
              size={6}
              value={f.responsibleIds}
              onChange={(e) =>
                setF({ ...f, responsibleIds: Array.from(e.target.selectedOptions).map((o) => o.value) })
              }
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
            >
              {userOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">SLA взяти (год)</span>
              <Input
                type="number"
                min={0}
                value={f.claimSla}
                onChange={(e) => setF({ ...f, claimSla: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">SLA виконати (год)</span>
              <Input
                type="number"
                min={0}
                value={f.completeSla}
                onChange={(e) => setF({ ...f, completeSla: e.target.value })}
              />
            </label>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={f.notifyInApp}
                onChange={(e) => setF({ ...f, notifyInApp: e.target.checked })}
              />
              In-app
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={f.notifyEmail}
                onChange={(e) => setF({ ...f, notifyEmail: e.target.checked })}
              />
              Email
            </label>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          {invalid && <p className="mr-auto text-xs text-destructive">{invalid}</p>}
          <Button variant="outline" size="sm" onClick={onClose}>
            Скасувати
          </Button>
          <Button
            size="sm"
            disabled={m.isPending || invalid !== null}
            onClick={() =>
              m.mutate(
                {
                  id: dept?.id,
                  name: f.name.trim(),
                  code: f.code.trim(),
                  active: f.active,
                  headId: f.headId || null,
                  responsibleIds: f.responsibleIds,
                  claimSlaDefaultHours: f.claimSla === "" ? null : Number(f.claimSla),
                  completeSlaDefaultHours: f.completeSla === "" ? null : Number(f.completeSla),
                  notifyInApp: f.notifyInApp,
                  notifyEmail: f.notifyEmail,
                },
                {
                  onSuccess: () => {
                    onToast("ok", dept ? "Відділ оновлено" : `Відділ «${f.name.trim()}» створено`);
                    onClose();
                  },
                  onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
                },
              )
            }
          >
            {m.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Зберегти
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── ABSORB: route form (createable НЕ тут — лише toggle+confirm, MP2) ──────

function RouteFormDialog({
  route,
  departments,
  presetDeptCode,
  onClose,
  onToast,
}: {
  route: AdminRouteRow | null;
  departments: RoutesDepartment[];
  presetDeptCode: string | null;
  onClose: () => void;
  onToast: (k: "ok" | "err", m: string) => void;
}) {
  const panelRef = useDrawerA11y(true, onClose);
  const m = useRoutesMutation("saveRoute");
  const [f, setF] = useState({
    requestType: route?.requestType ?? "",
    label: route?.label ?? "",
    department: route?.departmentCode ?? presetDeptCode ?? departments[0]?.code ?? "",
    enabled: route?.enabled ?? true,
    claimSla: route?.claimSlaHours?.toString() ?? "24",
    completeSla: route?.completeSlaHours?.toString() ?? "48",
    priority: route?.defaultPriority ?? ("Normal" as "Normal" | "High"),
  });

  const invalid =
    f.label.trim().length < 2
      ? "Вкажіть назву"
      : !/^[a-z0-9_]{2,100}$/.test(f.requestType.trim())
        ? "requestType: лише a-z, 0-9, _"
        : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={route ? `Маршрут ${route.label}` : "Новий маршрут"}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-card border bg-card p-4 shadow-xl outline-none"
      >
        <h3 className="mb-3 text-sm font-semibold">{route ? `Маршрут: ${route.label}` : "Новий маршрут"}</h3>
        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Назва (UA) *</span>
            <Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">requestType * (a-z, 0-9, _)</span>
            <Input
              value={f.requestType}
              disabled={route !== null}
              onChange={(e) => setF({ ...f, requestType: e.target.value })}
              className={cn("font-mono", route !== null && "bg-muted/40 text-muted-foreground")}
            />
            {route !== null && (
              <span className="mt-0.5 block text-[10px] text-muted-foreground">
                requestType незмінний — це ідентичність маршруту.
              </span>
            )}
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted-foreground">Відділ</span>
            <select
              value={f.department}
              onChange={(e) => setF({ ...f, department: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
            >
              {departments
                .filter((d) => d.active)
                .map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.name} ({d.code})
                  </option>
                ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={f.enabled}
              onChange={(e) => setF({ ...f, enabled: e.target.checked })}
            />
            Увімкнено
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">SLA взяти</span>
              <Input
                type="number"
                min={0}
                value={f.claimSla}
                onChange={(e) => setF({ ...f, claimSla: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">SLA виконати</span>
              <Input
                type="number"
                min={0}
                value={f.completeSla}
                onChange={(e) => setF({ ...f, completeSla: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-muted-foreground">Пріоритет</span>
              <select
                value={f.priority}
                onChange={(e) => setF({ ...f, priority: e.target.value as "Normal" | "High" })}
                className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
              >
                <option value="Normal">Normal</option>
                <option value="High">High</option>
              </select>
            </label>
          </div>
          {/* MP3: defaultAssignee — drawer-only read-model (person-routing AE) */}
          {route !== null && (
            <p className="rounded bg-muted/40 px-2.5 py-2 text-[11px] text-muted-foreground">
              defaultAssignee: {route.defaultAssigneeName ?? "—"} · person-routing (ActionEngine);
              P0 — read-only, основна модель = департамент-inbox.
            </p>
          )}
          {route === null && (
            <p className="rounded bg-muted/40 px-2.5 py-2 text-[11px] text-muted-foreground">
              Новий маршрут створюється з вимкненим прийманням заявок — увімкніть toggle після
              призначення відповідального (NULL=DENY).
            </p>
          )}
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          {invalid && <p className="mr-auto text-xs text-destructive">{invalid}</p>}
          <Button variant="outline" size="sm" onClick={onClose}>
            Скасувати
          </Button>
          <Button
            size="sm"
            disabled={m.isPending || invalid !== null}
            onClick={() =>
              m.mutate(
                {
                  id: route?.id,
                  requestType: f.requestType.trim(),
                  label: f.label.trim(),
                  department: f.department,
                  enabled: f.enabled,
                  claimSlaHours: f.claimSla === "" ? null : Number(f.claimSla),
                  completeSlaHours: f.completeSla === "" ? null : Number(f.completeSla),
                  defaultPriority: f.priority,
                },
                {
                  onSuccess: () => {
                    onToast("ok", route ? "Маршрут оновлено" : `Маршрут «${f.label.trim()}» створено`);
                    onClose();
                  },
                  onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
                },
              )
            }
          >
            {m.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Зберегти
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── shared ─────────────────────────────────────────────────────────────────

function Kv({ k, v }: { k: string; v: string | null }) {
  return (
    <div className="flex justify-between gap-2 py-1.5">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="text-right">{v ?? "—"}</dd>
    </div>
  );
}
